import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatChipsModule } from '@angular/material/chips';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { UsersService, UserListItemDto } from './users.service';
import { RoleDto } from '../admin/admin.models';

@Component({
  selector: 'app-users',
  standalone: true,
  imports: [
    CommonModule, FormsModule,
    MatFormFieldModule, MatInputModule, MatButtonModule, MatIconModule,
    MatTableModule, MatChipsModule, MatSelectModule, MatProgressSpinnerModule
  ],
  templateUrl: './users.component.html',
  styleUrl: './users.component.scss'
})
export class UsersComponent implements OnInit {
  private readonly usersApi = inject(UsersService);

  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly users = signal<UserListItemDto[]>([]);
  readonly roles = signal<RoleDto[]>([]);
  readonly canAssignRoles = signal(false);
  readonly selectedUserId = signal<string | null>(null);
  readonly savingRoles = signal(false);
  selectedRoleIds: string[] = [];

  searchText = '';
  readonly columns = ['displayName', 'email', 'status', 'roles', 'lastLoginAt', 'actions'];

  ngOnInit(): void {
    this.loadRoles();
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    this.usersApi.list({ page: 1, pageSize: 100, search: this.searchText || undefined }).subscribe({
      next: (page) => {
        this.users.set(page.items ?? []);
        this.loading.set(false);
        if ((page.items ?? []).some(u => !u.roles?.length && !u.roleIds?.length)) {
          this.hydrateRoles();
        }
      },
      error: (err) => {
        this.error.set(err?.message || 'Failed to load users.');
        this.loading.set(false);
      }
    });
  }

  loadRoles(): void {
    this.usersApi.listRoles().subscribe({
      next: (items) => {
        this.roles.set(Array.isArray(items) ? items : []);
        this.canAssignRoles.set(this.roles().length > 0);
      },
      error: () => {
        this.roles.set([]);
        this.canAssignRoles.set(false);
      }
    });
  }

  private hydrateRoles(): void {
    for (const user of this.users()) {
      if (user.roles?.length) continue;
      this.usersApi.getById(user.id).subscribe({
        next: (detail) => {
          this.users.update(list => list.map(u => u.id === user.id ? { ...u, roles: detail.roles ?? [] } : u));
        },
        error: () => undefined
      });
    }
  }

  selectUser(user: UserListItemDto): void {
    this.selectedUserId.set(user.id);
    this.usersApi.getById(user.id).subscribe({
      next: (detail) => {
        if (detail.roleIds?.length) {
          this.selectedRoleIds = [...detail.roleIds];
          return;
        }
        const names = new Set((detail.roles ?? []).map(r => r.toLowerCase()));
        this.selectedRoleIds = this.roles()
          .filter(r => names.has(r.name.toLowerCase()) || (r.code && names.has(r.code.toLowerCase())))
          .map(r => r.id);
      },
      error: () => { this.selectedRoleIds = []; }
    });
  }

  saveRoles(): void {
    const userId = this.selectedUserId();
    if (!userId) return;
    this.savingRoles.set(true);
    this.usersApi.assignRoles(userId, this.selectedRoleIds).subscribe({
      next: (updated) => {
        this.savingRoles.set(false);
        this.error.set(null);
        this.users.update(list => list.map(u => u.id === userId
          ? { ...u, roles: updated.roles ?? this.roles().filter(r => this.selectedRoleIds.includes(r.id)).map(r => r.name), roleIds: this.selectedRoleIds }
          : u));
        this.selectedUserId.set(null);
      },
      error: (err) => {
        this.error.set(err?.message || 'Failed to assign roles.');
        this.savingRoles.set(false);
      }
    });
  }

  roleNames(user: UserListItemDto): string[] {
    return user.roles ?? [];
  }
}
